import "./naf-insurance-panel.scss";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import nafInsurance1 from "assets/images/v2/naf-insurance-1.svg";
import nafInsurance2 from "assets/images/v2/naf-insurance-2.svg";
import nafInsuranceArrowRightFromBracket from "assets/images/v2/naf-insurance-arrow-right-from-bracket.svg";
import nafInsuranceLogo from "assets/images/naf-insurance-blue.png";
import { ReactComponent as NafInsuranceGreenCheck } from "assets/images/v2/naf-insurance-green-check.svg";
import nafInsuranceHomeUmbrella from "assets/images/v2/naf-insurance-home-umbrella.svg";
import { Button } from "components/ui/button/Button";
import {
  NAF_INSURANCE_MODAL_HEADER,
  NAF_INSURANCE_MODAL_REDIRECT_CONTINUE_BUTTON,
  NAF_INSURANCE_MODAL_DECLINE_BUTTON,
  NAF_INSURANCE_MODAL_DISCLAIMER_TEXT,
  NAF_INSURANCE_MODAL_SUCCESS_HEADER,
  NAF_INSURANCE_MODAL_SUCCESS_CLOSE_BUTTON,
  NAF_INSURANCE_MODAL_SUCCESS_SUBHEADER,
  NAF_INSURANCE_MODAL_SUCCESS_NEXT_STEP1,
  NAF_INSURANCE_MODAL_SUCCESS_NEXT_STEP2,
  NAF_INSURANCE_MODAL_BODY_LINE1,
  NAF_INSURANCE_MODAL_BODY_LINE2,
  NAF_INSURANCE_MODAL_PROCEED_TO_REDIRECT_BUTTON,
  NAF_INSURANCE_MODAL_REDIRECT_CLOSE_BUTTON,
  NAF_INSURANCE_MODAL_LOADING_TEXT_LINE1,
  NAF_INSURANCE_MODAL_LOADING_TEXT_LINE2,
} from "utilities/Constant";
import Drawer from "components/ui/drawer/Drawer";
import { setFSNavigatedToInsuranceCustomEvent } from "utilities/full-story";
import { useSelector } from "react-redux";
import { getNhDealApplicationId, getCurrentUserId } from "features/authSlice";
import { useGetInsuranceReferralQuery } from "pages/dashboard/home/dashboardService";
import { Spinner } from "components/v2/spinner/spinner";
import { getCachedInsurancePersonalizedUrl } from "pages/dashboard/hooks/useInsuranceEligibility";

export interface NafInsurancePanelProps {
  onClose: () => void;
  isLoadInitialState?: boolean;
  onContinue?: () => void | Promise<void>;
}

enum PanelViewMode {
  Initial = 0,
  Redirect = 1,
  Loading = 2,
  Success = 3
}

export default function NafInsurancePanel({
  onClose,
  onContinue,
}: Readonly<NafInsurancePanelProps>) {
  const nhDealApplicationId = useSelector(getNhDealApplicationId);
  const userId = useSelector(getCurrentUserId);
  const [isShowDisclaimer, setIsShowDisclaimer] = useState(false);
  const [panelViewIndex, setPanelViewIndex] = useState(PanelViewMode.Initial);

  useGetInsuranceReferralQuery(
    { nhDealApplicationId, userId },
    {
      skip:
        panelViewIndex !== PanelViewMode.Success ||
        !nhDealApplicationId ||
        !userId,
    },
  );
  const handleContinueClick = async () => {
    setPanelViewIndex(PanelViewMode.Redirect);
    await onContinue?.();
  };
  const handleRedirectContinueClick = () => {
    setPanelViewIndex(PanelViewMode.Loading);
    loadInsuranceReferralUrlNewTab();
    setPanelViewIndex(PanelViewMode.Success);
  };
  const handleClose = () => {
    setPanelViewIndex(PanelViewMode.Initial);
    setIsShowDisclaimer(false);
    onClose();
  };
  
  const openInNewTab = (url: string) => {
    const newTab = window.open(url, "_blank");
    if (!newTab) {
      console.error("Failed to open a new tab. The browser might be blocking pop-ups.");
    }
    return newTab;
  };

  const loadInsuranceReferralUrlNewTab = () => {
    try {
      const redirectUrl = getCachedInsurancePersonalizedUrl(nhDealApplicationId);
      const newTab = openInNewTab("/external");
      if (newTab && redirectUrl) {
        setFSNavigatedToInsuranceCustomEvent(nhDealApplicationId, redirectUrl);
        newTab.location.href = redirectUrl;
      }
    } catch (error) {
      console.error("Error in loading quick new tab URL:", error);
    }
  };

  const renderInitialView = () => (
    <>
      <h5 className="naf-insurance-modal__title heading-medium">{NAF_INSURANCE_MODAL_HEADER}</h5>
      <div className="naf-insurance-modal__content">
        <div className="naf-insurance-modal__picture">
          <img src={nafInsuranceHomeUmbrella} alt="Graphic of a house covered by a large umbrella" />
        </div>
        <div className="naf-insurance-modal__body label-alt-large-regular">
          <p>{NAF_INSURANCE_MODAL_BODY_LINE1}</p>
          <p>{NAF_INSURANCE_MODAL_BODY_LINE2}</p>
        </div>
        <div className="naf-insurance-modal__disclaimer">
          <div className="naf-insurance-modal__disclaimer-header">
            <Button
              title="Disclaimer"
              type="link-no-underline"
              className="disclaimer-title label-large-medium"
              onClick={() => setIsShowDisclaimer(!isShowDisclaimer)}
            />
            <Button
              className="disclaimer-caret"
              type="link-no-underline">
                <FontAwesomeIcon icon={isShowDisclaimer ? solid("chevron-up") : solid("chevron-down")} />
            </Button>
          </div>
          {isShowDisclaimer && (
            <p className="naf-insurance-modal__disclaimer-text label-alt-large-regular">{NAF_INSURANCE_MODAL_DISCLAIMER_TEXT}</p>
          )}
        </div>
      </div>
      <div className="naf-insurance-modal__action-button">
        <Button
          title={NAF_INSURANCE_MODAL_PROCEED_TO_REDIRECT_BUTTON}
          type="round"
          variant="primary"
          size="large"
          onClick={() => handleContinueClick()}
          className="label-large-medium"
        />
        <Button
          title={NAF_INSURANCE_MODAL_DECLINE_BUTTON}
          type="round"
          variant="outline-primary"
          size="large"
          onClick={() => handleClose()}
          className="label-large-medium"
        />
      </div>
    </>
  );

  const renderRedirectView = () => (
    <>
    <div className="naf-insurance-modal__redirect">
      <div className="naf-insurance-modal__redirect-picture">
        <img src={nafInsuranceArrowRightFromBracket} alt="Right arrow extending from a square bracket" />
      </div>
      <div className="naf-insurance-modal__redirect-text">
        <h5 className="heading-medium">You will be directed to our NAF Insurance site</h5>
      </div>
    </div>
    <div className="naf-insurance-modal__action-button">
      <Button
        title={NAF_INSURANCE_MODAL_REDIRECT_CONTINUE_BUTTON}
        type="round"
        variant="primary"
        size="large"
        onClick={() => handleRedirectContinueClick()}
        className="label-large-medium"
      />
      <Button
        title={NAF_INSURANCE_MODAL_REDIRECT_CLOSE_BUTTON}
        type="round"
        variant="outline-primary"
        size="large"
        onClick={() => handleClose()}
        className="label-large-medium"
      />
    </div>
    </>
  );

  const renderLoadingView = () => (
    <div className="naf-insurance-modal__loading">
      <div><Spinner isApi /></div>
      <div className="naf-insurance-modal__loading-text">
        <p className="label-large-medium">{NAF_INSURANCE_MODAL_LOADING_TEXT_LINE1}</p>
        <p className="label-alt-large-regular">{NAF_INSURANCE_MODAL_LOADING_TEXT_LINE2}</p>
      </div>
    </div>
  );

  const renderSuccessView = () => (
    <>
      <div className="naf-insurance-modal__picture">
        <div className="naf-insurance-modal__picture-container">
          <NafInsuranceGreenCheck />
        </div>
      </div>
      <h5 className="naf-insurance-modal__title heading-medium">{NAF_INSURANCE_MODAL_SUCCESS_HEADER}</h5>
      <div className="naf-insurance-modal__success-body label-alt-large-regular">
        <div>
          <p className="label-large-medium">{NAF_INSURANCE_MODAL_SUCCESS_SUBHEADER}</p>
        </div>
        <div className="naf-insurance-modal__success-body-whats-next-step">
          <img src={nafInsurance1} alt="Number one list item indicator" />
          <p>
            {NAF_INSURANCE_MODAL_SUCCESS_NEXT_STEP1}
          </p>
        </div>
        <div className="naf-insurance-modal__success-body-whats-next-step">
          <img src={nafInsurance2} alt="Number two list item indicator" />
          <p>
            {NAF_INSURANCE_MODAL_SUCCESS_NEXT_STEP2}
          </p>
        </div>
      </div>
      <div className="naf-insurance-modal__action-button success">
        <Button
          title={NAF_INSURANCE_MODAL_SUCCESS_CLOSE_BUTTON}
          type="round"
          variant="primary"
          size="large"
          onClick={() => handleClose()}
          className="label-large-medium"
        />
      </div>
    </>
  );

  const renderBody = () => {
    switch (panelViewIndex) {
      case PanelViewMode.Initial:
        return renderInitialView();
      
      case PanelViewMode.Redirect:
        return renderRedirectView();
      
      case PanelViewMode.Loading:
        return renderLoadingView();
      
      case PanelViewMode.Success:
        return renderSuccessView();
    }
  };

  return (
    <Drawer
      isOpen
      onClose={handleClose}
      centerContent
      customCss={
        `naf-insurance-modal-drawer ${panelViewIndex === PanelViewMode.Loading ? "no-header" : ""}`
      }
      title={
        panelViewIndex !== PanelViewMode.Loading && <img src={nafInsuranceLogo} alt="NAF Insurance Logo" className="naf-insurance-logo" />
      }
      >
      <div className={`naf-insurance-modal ${panelViewIndex === PanelViewMode.Success ? 'success' : ''}`}>
        {renderBody()}
      </div>
    </Drawer>    
  );
}
